import { create } from 'zustand';

export const BURETTE_DATA: Record<string, number> = {
  '0.0': 50.00, '0.5': 47.20, '1.0': 44.80, '1.5': 42.60, '2.0': 40.80,
  '2.5': 39.40, '3.0': 38.20, '3.5': 37.40, '4.0': 36.80, '4.5': 36.40, '5.0': 36.20,
};

export const MAX_EXPERIMENT_TIME = 5.0;

export function computeStateAtTime(t: number): { buretteReading: number; recordedData: Record<string, number> } {
  const clampedTime = Math.min(Math.max(t, 0), MAX_EXPERIMENT_TIME);
  const lowerIdx = Math.floor(clampedTime / 0.5);
  const upperIdx = Math.min(lowerIdx + 1, 10);
  const lowerTime = (lowerIdx * 0.5).toFixed(1);
  const upperTime = (upperIdx * 0.5).toFixed(1);
  const frac = (clampedTime % 0.5) / 0.5;
  const lowerReading = BURETTE_DATA[lowerTime] ?? 36.2;
  const upperReading = BURETTE_DATA[upperTime] ?? 36.2;
  const buretteReading = parseFloat((lowerReading + frac * (upperReading - lowerReading)).toFixed(2));

  // Rebuild recorded data for all 0.5-intervals up to current time
  const recordedData: Record<string, number> = { '0.0': 50.0 };
  for (let i = 1; i <= 10; i++) {
    const intervalTime = i * 0.5;
    if (intervalTime <= clampedTime) {
      const key = intervalTime.toFixed(1);
      recordedData[key] = BURETTE_DATA[key] ?? 36.2;
    }
  }

  return { buretteReading, recordedData };
}

export interface ZnH2SO4State {
  currentStep: number;
  isReacting: boolean;
  experimentTime: number;
  buretteReading: number;
  recordedData: Record<string, number>;
  highlightTime: number | null;
  experimentComplete: boolean;
  isPaused: boolean;
  playbackSpeed: number;

  setCurrentStep: (step: number) => void;
  startReaction: () => void;
  setPaused: (paused: boolean) => void;
  setPlaybackSpeed: (speed: number) => void;
  tick: (minutesPerTick: number) => void;
  setHighlightTime: (time: number | null) => void;
  resetExperiment: () => void;
  seekTime: (targetTime: number) => void;
  recordReading: (time: number, reading: number) => void;
}

export const useZnH2SO4Store = create<ZnH2SO4State>((set, get) => ({
  currentStep: 0,
  isReacting: false,
  experimentTime: 0,
  buretteReading: 50.0,
  recordedData: { '0.0': 50.0 },
  highlightTime: null,
  experimentComplete: false,
  isPaused: false,
  playbackSpeed: 1,

  setCurrentStep: (step) => set({ currentStep: step }),

  startReaction: () => set({
    currentStep: 4,
    isReacting: true,
    isPaused: false
  }),

  setPaused: (paused) => set({ isPaused: paused }),

  setPlaybackSpeed: (speed) => set({ playbackSpeed: speed }),

  setHighlightTime: (time) => set({ highlightTime: time }),

  tick: (minutesPerTick) => set((state) => {
    if (!state.isReacting || state.isPaused) return state;

    const newTime = Math.min(state.experimentTime + minutesPerTick, MAX_EXPERIMENT_TIME);
    const lowerIdx = Math.floor(newTime / 0.5);
    const upperIdx = Math.min(lowerIdx + 1, 10);
    
    // Safety fallback
    const lowerTime = (lowerIdx * 0.5).toFixed(1);
    const upperTime = (upperIdx * 0.5).toFixed(1);
    
    const frac = (newTime % 0.5) / 0.5;
    const lowerReading = BURETTE_DATA[lowerTime] ?? 36.2;
    const upperReading = BURETTE_DATA[upperTime] ?? 36.2;
    const buretteReading = lowerReading + frac * (upperReading - lowerReading);

    const prevInterval = Math.floor(state.experimentTime / 0.5 - minutesPerTick / 0.5);
    const currInterval = Math.floor(newTime / 0.5);
    const crossedInterval = currInterval > prevInterval && newTime < MAX_EXPERIMENT_TIME;
    const crossedTime = crossedInterval ? currInterval * 0.5 : null;

    const newRecordedData = { ...state.recordedData };
    if (crossedTime !== null) {
      const key = crossedTime.toFixed(1);
      newRecordedData[key] = BURETTE_DATA[key] ?? buretteReading;
    }

    return {
      experimentTime: newTime,
      buretteReading: parseFloat(buretteReading.toFixed(2)),
      recordedData: newRecordedData,
      highlightTime: crossedTime,
      experimentComplete: newTime >= MAX_EXPERIMENT_TIME,
      isReacting: newTime < MAX_EXPERIMENT_TIME,
      isPaused: newTime >= MAX_EXPERIMENT_TIME ? false : state.isPaused,
    };
  }),

  resetExperiment: () => set({
    currentStep: 0,
    isReacting: false,
    experimentTime: 0,
    buretteReading: 50.0,
    recordedData: { '0.0': 50.0 },
    highlightTime: null,
    experimentComplete: false,
    isPaused: false,
    playbackSpeed: 1,
  }),

  seekTime: (targetTime: number) => set((state) => {
    const clampedTime = Math.min(Math.max(targetTime, 0), MAX_EXPERIMENT_TIME);
    const { buretteReading, recordedData } = computeStateAtTime(clampedTime);
    const isComplete = clampedTime >= MAX_EXPERIMENT_TIME;
    // If seeking backward from completed, re-enter reacting + paused
    const wasComplete = state.experimentComplete && !state.isReacting;
    const isReacting = isComplete ? false : (wasComplete ? true : state.isReacting);
    
    return {
      experimentTime: clampedTime,
      buretteReading,
      recordedData: { ...state.recordedData, ...recordedData },
      experimentComplete: isComplete,
      isReacting,
      isPaused: isComplete ? false : true,
      highlightTime: null,
    };
  }),

  recordReading: (time: number, reading: number) => set((state) => {
    const key = time.toFixed(1);
    const newRecordedData = { ...state.recordedData, [key]: parseFloat(reading.toFixed(2)) };
    return { recordedData: newRecordedData, highlightTime: time };
  })
}));